import { Checkbox, Input } from '@mm/components';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import validator from 'validator';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import { CloseModalProps } from '../modal-helpers';
import confetiSrc from './confeti.png';
import styles from './CongratulationOnFreeCreditsModal.module.css';

export const CongratulationOnFreeCreditsModal = ({
  closeModal,
}: CloseModalProps) => {
  const companyId = useSelector(selectActiveCompanyId);
  const { id: userId, userAttributes } = useSelector(selectSessionSelector);

  const [websiteURL, setWebsiteURL] = useState('');
  const [marketingChallenge, setMarketingChallenge] = useState('');
  const [selectedMarketingMethods, setSelectedMarketingMethods] = useState<
    string[]
  >([]);
  const [showErrors, setShowErrors] = useState(false);

  const { updateUserAttributes } = useUsersCRUDActions();
  const { update: updateCompany } = useCompanyCRUDActions();

  const marketingMethods = [
    'Paid media (Facebook, Instagram, Snapchat)',
    'Outbound marketing',
    'Email marketing',
    'Search ads',
  ];
  const maxLength = 255;
  const isValidURL = validator.isURL(websiteURL.trim());
  const invalid =
    !isValidURL ||
    !marketingChallenge.trim() ||
    !selectedMarketingMethods.length;

  const onCheckboxClick = value => {
    if (selectedMarketingMethods.find(m => m === value)) {
      setSelectedMarketingMethods(
        selectedMarketingMethods.filter(m => m !== value),
      );
      return;
    }

    setSelectedMarketingMethods([...selectedMarketingMethods, value]);
  };

  const mappedMarketingMethods = selectedMarketingMethods.map(item =>
    item === 'Paid media (Facebook, Instagram, Snapchat)'
      ? 'paid media'
      : item.toLowerCase(),
  );

  const onSubmit = async () => {
    if (invalid) {
      setShowErrors(true);
      return;
    }

    await updateCompany({
      id: companyId,
      website: websiteURL.trim(),
      companyAttributes: {
        marketingChallenge,
        marketingMethods: mappedMarketingMethods.join(','),
      },
    });
    closeModal();
  };

  useEffect(() => {
    if (!userAttributes?.seenFreeCreditsModal) {
      updateUserAttributes(companyId, userId, {
        seenFreeCreditsModal: true,
      });
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <img src={confetiSrc} />
          <span className={styles.heading}>
            Congratulations
            <br />
            on 3K Free Credits
          </span>
          <img src={confetiSrc} className={styles.headerImg} />
        </div>
        <div className={styles.textCentered}>
          Use these credits to automatically capture leads from your website
          visitors and contact them with our automation tools.{' '}
        </div>
        <div className={styles.form}>
          <div className={styles.formBlock}>
            <div className={styles.title}>Your business URL</div>
            <Input
              type="text"
              css={{
                container: classNames(styles.input, {
                  [styles.invalidInput]: !isValidURL && showErrors,
                }),
              }}
              placeholder="yourdomain.com"
              value={websiteURL}
              onChange={e => setWebsiteURL(e.target.value)}
              maxLength={maxLength}
            />
            {!isValidURL && showErrors && (
              <div className={styles.inputErrorMessage}>Invalid URL</div>
            )}
          </div>
          <div className={styles.formBlock}>
            <div className={styles.title}>
              What is your biggest marketing challenge
            </div>
            <Input
              type="text"
              css={{
                container: classNames(styles.input, {
                  [styles.invalidInput]:
                    !marketingChallenge.trim() && showErrors,
                }),
              }}
              placeholder="My biggest marketing challenge..."
              value={marketingChallenge}
              onChange={e => setMarketingChallenge(e.target.value)}
              maxLength={maxLength}
            />
            {!marketingChallenge.trim() && showErrors && (
              <div className={styles.inputErrorMessage}>Required field</div>
            )}
          </div>
          <div className={styles.formBlock}>
            <div className={styles.title}>
              Select all that apply to your marketing methods:
            </div>
            {marketingMethods.map(method => (
              <Checkbox
                isChecked={!!selectedMarketingMethods.find(m => m === method)}
                key={method}
                name={method}
                label={method}
                className={styles.checkbox}
                labelHelpTextClassName={styles.checkboxLabel}
                onClick={() => onCheckboxClick(method)}
              />
            ))}
          </div>
        </div>
      </div>
      <PrimaryButton
        className={styles.button}
        onClick={onSubmit}
        disabled={showErrors && invalid}
      >
        Claim 3K Free Credits
      </PrimaryButton>
    </div>
  );
};
